<template>
  <div class="job-architecture-topbar-actions">
    <FiltersDrawerToggle
      v-if="pageHasFilters[route.name]"
      v-model="open"
      :is-loading="pageLoading"
      :are-filters-applied="areNonIgnorableFiltersApplied"
    />
    <JobArchitectureSearch />
    <div
      v-if="canEdit && isEditMode"
      class="job-architecture-edit-toolbar"
    >
      <span
        v-if="selectedProfileUlids.length"
        class="job-architecture-selected-count text-body-2 text-textDarkest"
      >
        {{ selectedProfilesLabel }}
      </span>
      <JobArchitectureProfileActions :selected-count="selectedProfileUlids.length" />
      <v-btn
        v-if="selectedProfileUlids.length"
        variant="text"
        color="brandPrimary"
        class="text-button-3"
        @click="clearSelectedProfileUlids"
      >
        Clear
      </v-btn>
    </div>
    <div
      v-else
      class="job-architecture-view-toolbar"
    >
      <span
        v-if="selectedProfileUlids.length"
        class="job-architecture-selected-count text-body-2 text-textDarkest"
      >
        {{ selectedProfilesLabel }}
      </span>
      <v-btn
        variant="outlined"
        height="36"
        rounded="lg"
        class="download-csv-btn"
        :loading="xlsxDownloadLoading"
        :disabled="xlsxDownloadLoading || pageLoading || !hasResults"
        @click="emit('download')"
      >
        <v-icon
          icon="$tray-arrow-down"
          class="mr-2"
          size="small"
        />
        Download CSV
      </v-btn>
      <DpplNewButton
        color="brandPrimary"
        variant="flat"
        class="compare-selected-btn text-button-2"
        :disabled="!selectedProfileUlids.length"
        @click="emit('compare')"
      >
        Compare
      </DpplNewButton>
      <v-btn
        v-if="selectedProfileUlids.length"
        variant="text"
        color="brandPrimary"
        class="text-button-3"
        @click="clearSelectedProfileUlids"
      >
        Clear
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppBase } from '@/stores/app/base';
import { useAppLoading } from '@/stores/app/loading';
import { useFilters } from '@/stores/filters/filters';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import FiltersDrawerToggle from '@/components/filter/FiltersDrawerToggle.vue';
import JobArchitectureSearch from '@/components/jobArchitecture/JobArchitectureSearch.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import JobArchitectureProfileActions from '@/components/jobArchitecture/JobArchitectureProfileActions.vue';

const COMPACT_SELECTION_LABEL_MAX_WIDTH = 1355;

defineProps({
  canEdit: {
    type: Boolean,
    default: false,
  },
  hasResults: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['download', 'compare']);

const route = useRoute();

const { pageHasFilters, windowWidth } = storeToRefs(useAppBase());
const { pageLoading } = storeToRefs(useAppLoading());
const { areNonIgnorableFiltersApplied } = storeToRefs(useFilters());
const { open } = storeToRefs(useFilterDrawer());

const jobArchitectureStore = useJobArchitecture();
const {
  xlsxDownloadLoading,
  selectedProfileUlids,
  isEditMode,
} = storeToRefs(jobArchitectureStore);
const { clearSelectedProfileUlids } = jobArchitectureStore;

const selectedProfilesLabel = computed(() => {
  const count = selectedProfileUlids.value.length;

  if (open.value && windowWidth.value < COMPACT_SELECTION_LABEL_MAX_WIDTH) {
    return `${count} selected`;
  }

  if (count === 1) {
    return '1 Job Profile selected';
  }

  return `${count} Job Profiles selected`;
});

</script>

<style scoped lang="less">
.job-architecture-topbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  min-height: 40px;
  gap: 8px;
  row-gap: 10px;
}

.job-architecture-edit-toolbar,
.job-architecture-view-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.job-architecture-selected-count {
  white-space: nowrap;
}

.compare-selected-btn {
  height: 40px !important;
  min-height: 40px !important;
}

.download-csv-btn {
  height: 40px !important;
  min-height: 40px !important;
  padding: 0 12px;
  border-radius: 8px !important;
  border: 1px solid @stroke-default;
  color: @text-dark;
  background: @white;
}
</style>
